"use client";

import { Attachment, useGetAttachmentsByDocId } from "@/api/attachment";
import StyledDrawer from "@/components/StyledDrawer";
import {
  CopyFromMasterConfig,
  mapMasterPositionToChild,
  TypeOptionPosition,
} from "@/lib/image";
import { cn } from "@/lib/utils";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { DuplicateFromMasterDrawer } from "./DuplicateFromMasterDrawer";
import type { ImageFormData } from "./MediaLibrary";

type ImportFromMasterDrawersProps = {
  pickerOpen: boolean;
  onPickerOpenChange: (open: boolean) => void;
  copyFromMaster: CopyFromMasterConfig;
  targetDocId: string;
  childType: TypeOptionPosition;
  childAttachments: Attachment[];
  computeImageName?: (data?: ImageFormData) => string;
  onDuplicated?: () => void;
  embedAttachmentOnSubmit?: boolean;
  disabled?: boolean;
};

export const ImportFromMasterDrawers = ({
  pickerOpen,
  onPickerOpenChange,
  copyFromMaster,
  targetDocId,
  childType,
  childAttachments,
  computeImageName,
  onDuplicated,
  embedAttachmentOnSubmit = false,
  disabled = false,
}: ImportFromMasterDrawersProps) => {
  const { t } = useTranslation();
  const { data: masterAttachments, isLoading } = useGetAttachmentsByDocId(
    copyFromMaster.masterDocId,
  );

  const [duplicateOpen, setDuplicateOpen] = useState(false);
  const [selectedMasterAttachment, setSelectedMasterAttachment] =
    useState<Attachment | null>(null);
  const [initialPosition, setInitialPosition] = useState(
    copyFromMaster.fallbackPosition,
  );

  const realMasterAttachments = useMemo(
    () => (masterAttachments ?? []).filter((a) => !a.isGenericImage),
    [masterAttachments],
  );

  const openDuplicateDrawer = (attachment: Attachment) => {
    const mappedPosition = mapMasterPositionToChild(
      attachment.position,
      copyFromMaster.positionMap,
      copyFromMaster.fallbackPosition,
    );
    setSelectedMasterAttachment(attachment);
    setInitialPosition(mappedPosition);
    setDuplicateOpen(true);
  };

  const handlePickerOpenChange = (open: boolean) => {
    onPickerOpenChange(open);
    if (!open) {
      setDuplicateOpen(false);
      setSelectedMasterAttachment(null);
    }
  };

  const handleDuplicated = () => {
    setDuplicateOpen(false);
    setSelectedMasterAttachment(null);
    onPickerOpenChange(false);
    onDuplicated?.();
  };

  return (
    <>
      <StyledDrawer
        title={t("mediaLibrary.masterImages.title")}
        maxWidth="sm:max-w-2xl"
        className="min-w-[500px]"
        sheetProps={{
          open: pickerOpen,
          onOpenChange: handlePickerOpenChange,
        }}
      >
        <p className="text-sm text-gray-500 mb-4">
          {t("mediaLibrary.importFromMaster.pickerHint")}
        </p>
        {isLoading ? (
          <p className="text-sm text-gray-500">{t("chargement")}</p>
        ) : realMasterAttachments.length === 0 ? (
          <p className="text-sm text-gray-500">
            {t("mediaLibrary.masterImages.empty")}
          </p>
        ) : (
          <div className="flex gap-3 flex-wrap pb-4">
            {realMasterAttachments.map((attachment) => {
              const previewUrl =
                attachment.mediumPublicUrl ??
                attachment.publicUrl ??
                attachment.largePublicUrl;
              return (
                <button
                  key={attachment.id}
                  type="button"
                  disabled={disabled}
                  className={cn(
                    "w-[140px] text-left rounded-lg border transition-colors p-1 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-400",
                    selectedMasterAttachment?.id === attachment.id &&
                      duplicateOpen
                      ? "border-slate-500 bg-slate-50 ring-2 ring-slate-400"
                      : "border-transparent hover:border-slate-300 hover:bg-slate-50",
                  )}
                  onClick={() => openDuplicateDrawer(attachment)}
                >
                  <div className="w-full aspect-square bg-gray-100 rounded-lg overflow-hidden">
                    {previewUrl && (
                      <img
                        src={previewUrl}
                        alt=""
                        className="w-full h-full object-contain"
                      />
                    )}
                  </div>
                  <p className="text-xs text-gray-500 mt-1.5 px-0.5 break-words whitespace-normal">
                    {t(attachment.position)}
                  </p>
             
             
                </button>
              );
            })}
          </div>
        )}
      </StyledDrawer>

      <DuplicateFromMasterDrawer
        open={duplicateOpen}
        stacked
        onOpenChange={(open) => {
          setDuplicateOpen(open);
          if (!open) setSelectedMasterAttachment(null);
        }}
        sourceAttachment={selectedMasterAttachment}
        targetDocId={targetDocId}
        childType={childType}
        initialPosition={initialPosition}
        childAttachments={childAttachments}
        computeImageName={
          computeImageName
            ? (position) => computeImageName({ position } as ImageFormData)
            : undefined
        }
        onSuccess={handleDuplicated}
        embedAttachmentOnSubmit={embedAttachmentOnSubmit}
        disabled={disabled}
      />
    </>
  );
};

/** @deprecated Use ImportFromMasterDrawers */
export const ImportFromMasterAction = ImportFromMasterDrawers;
